/**
 * Auth API wrapper — consumer-facing layer.
 * Feature code imports from THIS file, never from _generated/.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";

import { clearCompanyChosen } from "../company-session.js";
import type { APIError } from "../http.js";
import { qk } from "../query-keys.js";

import { apiRequest, setCsrfToken } from "./_client.js";

// ── Types (kept here so we can swap orval without breaking imports) ──

/**
 * Per-request accessible-store set, mirrored from the API response.
 *   {mode:"all"}    — tenant-wide role; no UI gating needed.
 *   {mode:"scoped"} — store-scoped role; storeIds is the active binding set.
 *                    Empty array = no stores assigned (show empty state).
 */
export type AccessibleStores = { mode: "all" } | { mode: "scoped"; storeIds: string[] };

export interface PublicUser {
  id: string;
  email: string;
  // Tenant-less users (0 companies) have neither until they create a company.
  tenantId: string | null;
  // Human-readable unique tenant code — the URL slug (/t/<code>/…). Null when tenant-less.
  tenantCode: string | null;
  role: string | null;
  // Null for tenant-less /me; populated once tenant context is established.
  accessibleStores: AccessibleStores | null;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: PublicUser;
  csrfToken: string;
}

export interface RefreshResponse {
  csrfToken: string;
}

export interface LogoutResponse {
  ok: true;
}

// ── Endpoint wrappers ────────────────────────────────────────────────

export const authApi = {
  login: (data: LoginRequest): Promise<LoginResponse> =>
    apiRequest<LoginResponse>({ method: "POST", url: "/api/v2/auth/login", data }),

  me: (signal?: AbortSignal): Promise<{ user: PublicUser; csrfToken: string }> =>
    apiRequest<{ user: PublicUser; csrfToken: string }>({
      method: "GET",
      url: "/api/v2/auth/me",
      signal,
    }),

  refresh: (): Promise<RefreshResponse> =>
    apiRequest<RefreshResponse>({ method: "POST", url: "/api/v2/auth/refresh" }),

  logout: (): Promise<LogoutResponse> =>
    apiRequest<LogoutResponse>({ method: "POST", url: "/api/v2/auth/logout" }),
};

// ── React-Query hooks ────────────────────────────────────────────────

export function useMeQuery(
  options?: Omit<UseQueryOptions<PublicUser, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<PublicUser, APIError>({
    queryKey: qk.auth.me,
    queryFn: async ({ signal }) => {
      const resp = await authApi.me(signal);
      // Wire CSRF token on every /me — keeps the in-memory token alive
      // across page reloads (the auth cookie persists, but JS memory doesn't).
      setCsrfToken(resp.csrfToken);
      return resp.user;
    },
    retry: false,
    staleTime: 5 * 60_000,
    // Detect role/store membership changes and cookie changes in another tab.
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    refetchInterval: (query) => (query.state.data ? 60_000 : false),
    ...options,
  });
}

export function useLoginMutation(
  options?: Omit<UseMutationOptions<LoginResponse, APIError, LoginRequest>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<LoginResponse, APIError, LoginRequest>({
    ...options,
    mutationFn: authApi.login,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      setCsrfToken(data.csrfToken);
      qc.setQueryData(qk.auth.me, data.user);
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useLogoutMutation(
  options?: Omit<UseMutationOptions<LogoutResponse, APIError, void>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<LogoutResponse, APIError, void>({
    ...options,
    mutationFn: () => authApi.logout(),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      setCsrfToken(null);
      clearCompanyChosen();
      qc.setQueryData(qk.auth.me, null);
      qc.clear();
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useRefreshMutation() {
  return useMutation<RefreshResponse, APIError>({
    mutationFn: authApi.refresh,
    onSuccess: (data) => setCsrfToken(data.csrfToken),
  });
}

// ── 4B-1: signup / verify / resend ──────────────────────────────────

export interface SignupStartRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  /** A valid pending invite for this email → auto-verify + auto-login. */
  invitationToken?: string;
}
export interface OkResponse {
  ok: true;
  devLink?: string;
}
/**
 * `verified: true` means an invited signup was auto-verified and logged in
 * (cookie already set) — `user` is present and the caller should go straight to
 * the accept flow. `verified: false` is the normal "check your email" case.
 */
export interface SignupStartResponse {
  ok: true;
  verified: boolean;
  user?: PublicUser;
  csrfToken?: string;
  devLink?: string;
}

export const authApiSignup = {
  signupStart: (data: SignupStartRequest): Promise<SignupStartResponse> =>
    apiRequest({ method: "POST", url: "/api/v2/auth/signup/start", data }),

  signupVerify: (data: { token: string }): Promise<LoginResponse> =>
    apiRequest({ method: "POST", url: "/api/v2/auth/signup/verify", data }),

  signupResend: (data: { email: string; invitationToken?: string }): Promise<OkResponse> =>
    apiRequest({ method: "POST", url: "/api/v2/auth/signup/resend", data }),
};

export function useSignupStartMutation(
  options?: Omit<
    UseMutationOptions<SignupStartResponse, APIError, SignupStartRequest>,
    "mutationFn"
  >,
) {
  return useMutation<SignupStartResponse, APIError, SignupStartRequest>({
    ...options,
    mutationFn: authApiSignup.signupStart,
  });
}

export function useSignupVerifyMutation(
  options?: Omit<UseMutationOptions<LoginResponse, APIError, { token: string }>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<LoginResponse, APIError, { token: string }>({
    ...options,
    mutationFn: authApiSignup.signupVerify,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      // Verify auto-logs-in: seed the same CSRF + me cache login does so the
      // app treats the user as authenticated without a /me round-trip.
      setCsrfToken(data.csrfToken);
      qc.setQueryData(qk.auth.me, data.user);
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useSignupResendMutation(
  options?: Omit<
    UseMutationOptions<OkResponse, APIError, { email: string; invitationToken?: string }>,
    "mutationFn"
  >,
) {
  return useMutation<OkResponse, APIError, { email: string; invitationToken?: string }>({
    ...options,
    mutationFn: authApiSignup.signupResend,
  });
}

// ── 4B-1: forgot / reset password ───────────────────────────────────

export const authApiPassword = {
  forgot: (data: { email: string }): Promise<OkResponse> =>
    apiRequest({ method: "POST", url: "/api/v2/auth/password/forgot", data }),

  reset: (data: { token: string; newPassword: string }): Promise<{ ok: true }> =>
    apiRequest({ method: "POST", url: "/api/v2/auth/password/reset", data }),
};

export function useForgotPasswordMutation(
  options?: Omit<UseMutationOptions<OkResponse, APIError, { email: string }>, "mutationFn">,
) {
  return useMutation<OkResponse, APIError, { email: string }>({
    ...options,
    mutationFn: authApiPassword.forgot,
  });
}

export function useResetPasswordMutation(
  options?: Omit<
    UseMutationOptions<{ ok: true }, APIError, { token: string; newPassword: string }>,
    "mutationFn"
  >,
) {
  return useMutation<{ ok: true }, APIError, { token: string; newPassword: string }>({
    ...options,
    mutationFn: authApiPassword.reset,
  });
}

// ── 4B-1: logout-all + sessions ─────────────────────────────────────

export interface SessionInfo {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  lastSeenAt: string;
  createdAt: string;
  expiresAt: string | null;
  isCurrent: boolean;
}
export interface SessionsListResponse {
  items: SessionInfo[];
}

export const authApiSessions = {
  logoutAll: (): Promise<{ ok: true }> =>
    apiRequest({ method: "POST", url: "/api/v2/auth/logout-all" }),

  list: (signal?: AbortSignal): Promise<SessionsListResponse> =>
    apiRequest({ method: "GET", url: "/api/v2/auth/sessions", signal }),

  revoke: (sessionId: string): Promise<{ ok: true }> =>
    apiRequest({
      method: "POST",
      url: `/api/v2/auth/sessions/${encodeURIComponent(sessionId)}/revoke`,
    }),
};

export function useLogoutAllMutation() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, APIError, void>({
    mutationFn: () => authApiSessions.logoutAll(),
    onSuccess: () => {
      setCsrfToken(null);
      clearCompanyChosen();
      qc.clear();
    },
  });
}

export function useSessionsQuery() {
  return useQuery<SessionsListResponse, APIError>({
    queryKey: qk.auth.sessions,
    queryFn: ({ signal }) => authApiSessions.list(signal),
    retry: false,
  });
}

export function useRevokeSessionMutation() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, APIError, string>({
    mutationFn: (sessionId) => authApiSessions.revoke(sessionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.auth.sessions });
    },
  });
}

// ── 4B-2: tenant switcher ───────────────────────────────────────────

export interface SwitchTenantRequest {
  tenantId: string;
}

export const authApiSwitch = {
  switchTenant: (data: SwitchTenantRequest): Promise<LoginResponse> =>
    apiRequest({ method: "POST", url: "/api/v2/auth/switch-tenant", data }),
};

export function useSwitchTenantMutation() {
  const qc = useQueryClient();
  return useMutation<LoginResponse, APIError, SwitchTenantRequest>({
    mutationFn: authApiSwitch.switchTenant,
    onSuccess: (data) => {
      setCsrfToken(data.csrfToken);
      qc.setQueryData(qk.auth.me, data.user);
      // Tenant changed — drop everything tenant-scoped so it refetches.
      // Auth-scoped queries (me, sessions) stay because tenant change does
      // not invalidate the user identity.
      qc.removeQueries({
        predicate: (q) => {
          const k = q.queryKey[0];
          return k !== "auth";
        },
      });
    },
  });
}
